
import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAnalogHorrorMessage } from "./useAnalogHorrorMessage";
import { getPersonalityStyle } from "./AnalogHorrorStyles";
import { AnalogHorrorContent } from "./AnalogHorrorContent";
import { AIPersonality } from "@/components/settings/AIPersonalityPanel";

interface AnalogHorrorPopupProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type: "success" | "warning" | "achievement" | "level" | "weight" | "diet";
  aiPersonality?: AIPersonality;
}

export const AnalogHorrorPopup = ({ 
  isOpen, 
  onClose, 
  message,
  type = "success",
  aiPersonality = "ultraterrestrial"
}: AnalogHorrorPopupProps) => {
  const {
    glitching,
    isLoadingMessage,
    isSaving,
    displayMessage,
    handleSaveMotivation
  } = useAnalogHorrorMessage(isOpen, message, aiPersonality);

  if (!message) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-[999] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" 
        />
        
        <DialogPrimitive.Content 
          className={`fixed left-[50%] top-[50%] z-[1000] w-[81%] max-w-sm translate-x-[-50%] translate-y-[-50%] p-4 py-3 shadow-xl animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 overflow-visible ${getPersonalityStyle(aiPersonality)} border-2 border-gray-700`}
          style={{
            boxShadow: "0 0 30px rgba(255, 0, 0, 0.3), 0 0 50px rgba(0, 0, 0, 0.7)",
            filter: glitching ? "hue-rotate(90deg) contrast(1.5) brightness(1.2)" : "none",
            transition: "filter 0.1s ease-out",
            maxHeight: "80vh"
          }}
        >
          {/* Scan lines overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-20 z-[1001]" 
               style={{
                 backgroundImage: "repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8) 1px, transparent 1px, transparent 2px)",
                 backgroundSize: "100% 2px"
               }} />
          
          <AnalogHorrorContent
            messageType={type}
            displayMessage={displayMessage}
            isLoadingMessage={isLoadingMessage}
            isSaving={isSaving}
            glitching={glitching}
            onSave={handleSaveMotivation}
            onClose={onClose}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
